import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { FloatingPlayer } from "../components/FloatingPlayer";
import { TuneProvider } from "@/components/TunePanel";
import { EditionBar } from "@/components/layout/EditionBar";
import { Masthead } from "@/components/layout/Masthead";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { useCloudBookmarkSync } from "@/hooks/use-cloud-bookmark-sync";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        <p className="eyebrow">404</p>
        <h1 className="mt-3 font-serif text-3xl">Denne side findes ikke</h1>
        <p className="mt-3 meta">Måske gik du forkert i gaden. Gå tilbage til forsiden.</p>
        <Link to="/" className="mt-6 inline-block border-b border-bordeaux pb-0.5 text-bordeaux">
          Til forsiden
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-2xl">Noget faldt til ro på en forkert måde</h1>
        <p className="mt-3 meta">Prøv igen om et øjeblik.</p>
        <button
          onClick={() => { router.invalidate(); reset(); }}
          className="mt-6 border-b border-bordeaux pb-0.5 text-bordeaux"
        >
          Prøv igen
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Flâneriet – et samlingspunkt for langsomt liv" },
      { name: "description", content: "Flâneriet: at drive uden mål gennem byen, bogen, glasset. Et personligt, kurateret samlingspunkt for vin, kunst, jazz, poesi, litteratur, mad og cykling." },
      { property: "og:title", content: "Flâneriet – et samlingspunkt for langsomt liv" },
      { property: "og:description", content: "Flâneriet: at drive uden mål gennem byen, bogen, glasset. Et personligt, kurateret samlingspunkt for vin, kunst, jazz, poesi, litteratur, mad og cykling." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Flâneriet" },
      { property: "og:locale", content: "da_DK" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Flâneriet – et samlingspunkt for langsomt liv" },
      { name: "twitter:description", content: "Flâneriet: at drive uden mål gennem byen, bogen, glasset. Et personligt, kurateret samlingspunkt for vin, kunst, jazz, poesi, litteratur, mad og cykling." },
      { name: "theme-color", content: "#1a1a17" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Flâneriet",
          url: "https://flaneriet.online",
          inLanguage: "da-DK",
          description: "Et personligt, kurateret samlingspunkt for langsomt liv – vin, kunst, jazz, poesi, litteratur, mad og cykling.",
        }),
      },
    ],

    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/icons/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/icons/favicon-16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/icons/apple-touch-icon.png" },
      { rel: "manifest", href: "/icons/site.webmanifest" },

      // Preconnect til de tredjepartsoprindelser vi bruger på hver side.
      // Spotify-embed loades først når brugeren klikker på pladespilleren,
      // så den holder vi ude af preconnect for at spare TCP+TLS ved cold load.
      { rel: "preconnect", href: "https://api.open-meteo.com", crossOrigin: "" },
      { rel: "preconnect", href: "https://evtejmblzaqcwixaohgg.supabase.co", crossOrigin: "" },

    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="da">
      <head><HeadContent /></head>
      <body>{children}<Scripts /></body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useCloudBookmarkSync();
  return (
    <QueryClientProvider client={queryClient}>
      <TuneProvider>
        <div className="min-h-screen bg-paper text-ink">
          <div className="site-canvas">
            <EditionBar />
            <Masthead />
            <SiteNav />
            <main className="slow-fade content-canvas pb-8"><Outlet /></main>
            <SiteFooter />
          </div>
        </div>
        <FloatingPlayer />
      </TuneProvider>
    </QueryClientProvider>

  );
}
